import { Zap, Scissors, Camera } from "lucide-react";
import { Reveal, SectionTag } from "@/components/Reveal";
import { IMG } from "@/lib/site";

const steps = [
  {
    num: "01",
    icon: Zap,
    title: "Jet it",
    color: "#adff01",
    text: "A specialised hose with a 5000 PSI jetting head goes down your line and blasts the blockage apart — not just poked through.",
  },
  {
    num: "02",
    icon: Scissors,
    title: "Cut it",
    color: "#ff66c4",
    text: "Tree roots, grease, heavy build-up — the jetting head cuts straight through and thoroughly cleans the inside of your pipework.",
  },
  {
    num: "03",
    icon: Camera,
    title: "Prove it",
    color: "#169ed9",
    text: "CCTV camera inspection and locating equipment confirm the cause, so the fix lasts longer — not just until next month.",
  },
];

export default function Process() {
  return (
    <section id="process" data-testid="process-section" className="border-b-2 border-white/10 py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <SectionTag number="02" label="Our Process" color="#ff66c4" />

        <div className="grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <h2 className="font-display text-4xl uppercase leading-[0.9] tracking-tight sm:text-6xl">
                A dedicated crew for{" "}
                <span className="text-[#ff66c4]">blocked drains.</span> Nothing else.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-300 md:text-lg">
              <p data-testid="process-copy">
                If more than one fixture is draining slowly, the blockage is usually too
                severe for a standard plunge — advanced equipment is required. Our trained
                blocked drain specialists use high-pressure drain jetting capable of cutting
                through tree roots, clearing heavy blockages, and cleaning the inside of your
                pipework for a longer-lasting result. For all blocked drains in Perth — book
                your job with us.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="shadow-hard-pink border-2 border-white/15">
              <video
                poster={IMG.technician}
                autoPlay
                muted
                loop
                playsInline
                className="h-64 w-full object-cover md:h-80"
                data-testid="process-video"
              >
                <source src="/process.webm" type="video/webm" />
                <source src="/process.mp4" type="video/mp4" />
              </video>
            </div>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-3">
          {steps.map(({ num, icon: Icon, title, color, text }, i) => (
            <Reveal key={num} delay={i * 0.12}>
              <article
                data-testid={`process-step-${num}`}
                className="group h-full border-2 border-white/10 bg-[#121214] p-8 transition-colors hover:border-white/30"
              >
                <div className="flex items-center justify-between">
                  <span className="font-display text-5xl" style={{ color }}>
                    {num}
                  </span>
                  <span
                    className="flex h-12 w-12 items-center justify-center border-2 transition-transform group-hover:-translate-y-1"
                    style={{ borderColor: color, background: `${color}1a` }}
                  >
                    <Icon className="h-6 w-6" style={{ color }} strokeWidth={2.5} />
                  </span>
                </div>
                <h3 className="mt-6 font-display text-2xl uppercase tracking-tight">
                  {title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-zinc-400">{text}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
